import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from 'react'
import type { MoreFieldMappingItem } from 'src/interfaces/Template.interface'
import {
  deriveMiscFieldRows,
  mergeMiscTemplates,
  selectMiscTemplatesForEdit,
  stripMiscSavedTemplates,
  type MiscHydrateStore,
  type MiscTemplateRef,
} from '../utils/miscMappingHydrate'

type CatalogCommitResult = 'applied' | 'empty' | 'stale'

type MiscAiOverlay = {
  recipeId: number | null
  rows: MoreFieldMappingItem[]
}

/**
 * Miscellaneous mapping edit state.
 * The channel field catalog and the saved templates are stored separately and
 * combined when both are available, so whichever request finishes first still hydrates.
 */
export const useMiscMappingEditState = ({
  viewMode,
  editingRecipeId,
  platformId,
  sourceFieldsReady = true,
}: {
  viewMode: 'list' | 'create' | 'edit'
  editingRecipeId: number | null
  platformId: number | null | undefined
  /** False while source native/custom lists used by mapped misc fields are still loading. */
  sourceFieldsReady?: boolean
}) => {
  const catalogRequestRef = useRef(0)
  const editGenerationRef = useRef(0)
  const activeEditRecipeRef = useRef<number | null>(null)
  const catalogRef = useRef<MoreFieldMappingItem[]>([])
  const catalogPlatformIdRef = useRef<number | null>(null)

  const [catalog, setCatalog] = useState<MoreFieldMappingItem[]>([])
  const [catalogPlatformId, setCatalogPlatformId] = useState<number | null>(null)
  const [catalogPending, setCatalogPending] = useState(false)
  const [templatesPending, setTemplatesPending] = useState(false)
  const [store, setStore] = useState<MiscHydrateStore | null>(null)
  const [failedRefs, setFailedRefs] = useState<MiscTemplateRef[]>([])
  const [overlay, setOverlay] = useState<MiscAiOverlay | null>(null)

  catalogRef.current = catalog
  catalogPlatformIdRef.current = catalogPlatformId

  const templates = selectMiscTemplatesForEdit(store, editingRecipeId, platformId)
  const overlayRows =
    overlay && overlay.recipeId === editingRecipeId ? overlay.rows : null

  const derivedRows = useMemo(
    () =>
      deriveMiscFieldRows({
        catalog,
        templates,
        overlay: overlayRows,
      }),
    [catalog, templates, overlayRows]
  )

  const derivedRowsRef = useRef(derivedRows)
  derivedRowsRef.current = derivedRows
  const editingRecipeIdRef = useRef(editingRecipeId)
  editingRecipeIdRef.current = editingRecipeId

  const miscSectionBusy =
    viewMode === 'edit' && (templatesPending || catalogPending || !sourceFieldsReady)
  const moreFieldMappingData = miscSectionBusy ? [] : derivedRows
  const showMoreMapping = !miscSectionBusy && derivedRows.length > 0

  const beginCatalogLoad = (nextPlatformId: number) => {
    const requestId = ++catalogRequestRef.current
    const warm =
      catalogPlatformIdRef.current === nextPlatformId && catalogRef.current.length > 0
    if (!warm) setCatalogPending(true)
    return requestId
  }

  const commitCatalog = (
    requestId: number,
    nextPlatformId: number,
    fields: MoreFieldMappingItem[]
  ): CatalogCommitResult => {
    if (requestId !== catalogRequestRef.current) return 'stale'
    if (!Array.isArray(fields) || fields.length === 0) {
      setCatalogPending(false)
      return 'empty'
    }
    const next = stripMiscSavedTemplates(fields)
    catalogRef.current = next
    catalogPlatformIdRef.current = nextPlatformId
    setCatalog(next)
    setCatalogPlatformId(nextPlatformId)
    setCatalogPending(false)
    return 'applied'
  }

  const abandonCatalogLoad = (requestId: number) => {
    if (requestId !== catalogRequestRef.current) return
    setCatalogPending(false)
  }

  const prepareMiscEdit = (nextPlatformId: number | null, recipeId: number) => {
    const generation = ++editGenerationRef.current
    activeEditRecipeRef.current = recipeId
    setOverlay(null)
    setStore(null)
    setFailedRefs([])
    setTemplatesPending(true)
    const warm =
      nextPlatformId != null &&
      catalogPlatformIdRef.current === nextPlatformId &&
      catalogRef.current.length > 0
    if (!warm && nextPlatformId != null) setCatalogPending(true)
    return generation
  }

  const acceptMiscTemplates = (
    generation: number,
    loaded: MiscHydrateStore,
    options?: { settled?: boolean }
  ) => {
    if (generation !== editGenerationRef.current) return
    if (loaded.recipeId !== activeEditRecipeRef.current) return
    const settled = options?.settled !== false
    setStore((current) => {
      const sameEdit =
        current != null &&
        current.recipeId === loaded.recipeId &&
        current.platformId === loaded.platformId
      return {
        recipeId: loaded.recipeId,
        platformId: loaded.platformId,
        templates: mergeMiscTemplates(
          sameEdit && current ? current.templates : [],
          loaded.templates
        ),
      }
    })
    if (Array.isArray(loaded.failedRefs)) setFailedRefs(loaded.failedRefs)
    // A partial batch must not reveal the grid: some rows would show saved
    // values while the rest still look empty.
    setTemplatesPending(!settled)
  }

  /** Clears the template spinner if this edit is still the active one. */
  const settleMiscTemplates = (generation: number) => {
    if (generation !== editGenerationRef.current) return
    setTemplatesPending(false)
  }

  const clearMiscEditMappings = () => {
    activeEditRecipeRef.current = null
    editGenerationRef.current += 1
    setStore(null)
    setOverlay(null)
    setFailedRefs([])
    setTemplatesPending(false)
  }

  const setMiscAiOverlay: Dispatch<SetStateAction<MoreFieldMappingItem[]>> = useCallback(
    (action) => {
      setOverlay((current) => {
        const base =
          current && current.recipeId === editingRecipeIdRef.current
            ? current.rows
            : derivedRowsRef.current
        const rows = typeof action === 'function' ? action(base) : action
        return { recipeId: editingRecipeIdRef.current, rows }
      })
    },
    []
  )

  return {
    moreFieldMappingData,
    showMoreMapping,
    miscSectionBusy,
    beginCatalogLoad,
    commitCatalog,
    abandonCatalogLoad,
    failedMiscTemplateRefs: failedRefs,
    prepareMiscEdit,
    acceptMiscTemplates,
    settleMiscTemplates,
    clearMiscEditMappings,
    setMiscAiOverlay,
  }
}
